/**
 * ClinicRow — one clinic in the compact table (status-tinted row, design §6).
 *
 * Columns (RTL order): clinic (avatar · name · owner/patients · tags + add)
 * → payment (amount before VAT · cycle · הו"ק) → next payment (standing-order
 * charge date or planEndDate) → lifetime revenue → last contact (click = log
 * a contact) → last login → caret.
 *
 * Row click opens the detail page — every inner control stops propagation.
 * Inactive variant swaps the data cells for "הפעל מחדש".
 */

import { useState } from "react";
import {
  ArrowCounterClockwise,
  Buildings,
  CaretLeft,
  CreditCard,
  HandCoins,
  Plus,
  SignOut,
  WarningCircle,
} from "@phosphor-icons/react";
import HoverTip from "../../../../components/ui/HoverTip";
import ClinicTags from "../shared/ClinicTags";
import { UserTagChip } from "../shared/ClinicUserTags";
import {
  CONTACT_CHANNEL_LABELS,
  formatDate,
  formatShekels,
  getContactStatus,
  getNextPayment,
  getPayingSummary,
  getPaymentAlert,
  getRelativeDate,
  getRowStatus,
  loginTone,
  relativeDaysLabel,
  sumClinicRevenue,
  VAT_PERCENT_LABEL,
} from "../shared/nivDerived";
import { AiSummaryPeek, NotePeek } from "../shared/RowQuickActions";
import ContactQuickLogPopover from "./ContactQuickLogPopover";
import TagAssignPopover from "./TagAssignPopover";

const ROW_TAG_KEYS = ["plan", "trialDays", "churned", "seats"];

/** Short Hebrew date without year ("15 בנוב׳") for the dense cells. */
const shortDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("he-IL", { day: "numeric", month: "short" });
};

function Cell({ col, label, children, className = "", onClick }) {
  return (
    <div className={`nivck2-cell nivck2-cell-${col} ${className}`.trim()} role="cell" onClick={onClick}>
      <span className="nivck2-cell-label">{label}</span>
      {children}
    </div>
  );
}

function ClinicRow({
  row,
  usage,
  pendingStandingOrderMonths = 0,
  tagMap,
  tags,
  author,
  variant = "default",
  onOpen,
  onManageTags,
  onReactivate,
}) {
  const [popover, setPopover] = useState(null); // "tags" | "contact"
  const [justAdded, setJustAdded] = useState(() => new Set());

  const status = getRowStatus(row);
  const crm = row.crm || {};
  const paying = getPayingSummary(crm, row.planEndDate);
  const paymentAlert = getPaymentAlert(row);
  const next = getNextPayment(row, pendingStandingOrderMonths);
  const revenue = sumClinicRevenue(crm);
  const contact = getContactStatus(row);
  const loginDays = usage?.logins?.daysSinceLastLogin;
  const userTags = (crm.tagIds || []).map((id) => tagMap[id]).filter(Boolean);

  const stop = (e) => e.stopPropagation();
  const togglePopover = (e, kind) => {
    stop(e);
    setPopover((p) => (p === kind ? null : kind));
  };

  return (
    <div
      className={`nivck2-row nivck2-row-${status}${variant === "inactive" ? " is-inactive" : ""}`}
      role="row"
      tabIndex={0}
      onClick={() => onOpen(row)}
      onKeyDown={(e) => e.key === "Enter" && onOpen(row)}
    >
      {/* Clinic */}
      <div className="nivck2-cell nivck2-cell-clinic" role="cell">
        <div className="nivck2-avatar">
          {row.logoUrl ? <img src={row.logoUrl} alt="" /> : <Buildings size={16} />}
        </div>
        <div className="nivck2-row-main">
          <div className="nivck2-row-name-line">
            <strong className="nivck2-row-name">{row.name || "ללא שם"}</strong>
            <span className="nivck2-row-peeks" onClick={stop}>
              <NotePeek text={crm.freeNotes} updatedAt={crm.freeNotesUpdatedAt} />
              <AiSummaryPeek aiSummary={crm.aiSummary} />
            </span>
          </div>
          <div className="nivck2-row-meta">
            <span>{row.ownerName || "—"}</span>
            <span className="nivck2-meta-sep" />
            <span>{row.totalPatients} מטופלים</span>
            {variant === "inactive" && row.inactiveByAdminAt && (
              <>
                <span className="nivck2-meta-sep" />
                <span>הושבתה ב-{formatDate(row.inactiveByAdminAt)}</span>
              </>
            )}
          </div>
          <div className="nivck2-row-tags">
            <ClinicTags row={row} keys={ROW_TAG_KEYS} />
            {userTags.map((tag) => (
              <UserTagChip key={tag.id} tag={tag} pop={justAdded.has(tag.id)} />
            ))}
            {variant !== "inactive" && (
              <span className="nivck2-pop-anchor" onClick={stop}>
                <button
                  type="button"
                  className="nivck2-tag-add"
                  title="תגיות"
                  aria-label="הוסף תגית"
                  onClick={(e) => togglePopover(e, "tags")}
                >
                  <Plus size={11} weight="bold" />
                </button>
                {popover === "tags" && (
                  <TagAssignPopover
                    clinicId={row.clinicId}
                    tagIds={crm.tagIds || []}
                    tags={tags}
                    onClose={() => setPopover(null)}
                    onManage={onManageTags}
                    onAssigned={(tagId) => setJustAdded((prev) => new Set(prev).add(tagId))}
                  />
                )}
              </span>
            )}
          </div>
        </div>
      </div>

      {variant === "inactive" ? (
        <div className="nivck2-cell nivck2-cell-actions" role="cell" onClick={stop}>
          <button
            type="button"
            className="nivck2-btn-secondary"
            onClick={(e) => {
              stop(e);
              onReactivate(row);
            }}
          >
            <ArrowCounterClockwise size={14} weight="bold" />
            הפעל מחדש
          </button>
        </div>
      ) : (
        <>
          {/* Payment */}
          <Cell col="payment" label="תשלום">
            {paying.status === "paying" ? (
              <HoverTip
                text={`${formatShekels(paying.amountPreVat)} לפני ${VAT_PERCENT_LABEL}${
                  paying.cycleLabel ? ` · ${paying.cycleLabel}` : ""
                }${paying.isStandingOrder ? ' · הו"ק באשראי' : ""}${
                  paymentAlert ? ` · ${paymentAlert.label}` : ""
                }`}
              >
                <span className={`nivck2-money nivck2-money-paying${paymentAlert ? " is-alert" : ""}`}>
                  {paymentAlert ? <WarningCircle size={13} weight="fill" /> : <HandCoins size={13} weight="fill" />}
                  <span className="nivck2-money-main">{formatShekels(paying.amountPreVat)}</span>
                  <span className="nivck2-money-sub">
                    {paying.cycleLabel || "לפני מע״מ"}
                    {paying.isStandingOrder && (
                      <CreditCard size={11} weight="fill" aria-label='הו"ק' />
                    )}
                  </span>
                </span>
              </HoverTip>
            ) : (
              <span className={`nivck2-money nivck2-money-${paying.status === "left" ? "left" : "none"}`}>
                {paying.status === "left" && <SignOut size={12} weight="bold" />}
                {paying.statusLabel}
              </span>
            )}
          </Cell>

          {/* Next payment */}
          <Cell col="next" label="תשלום הבא">
            {next ? (
              <HoverTip
                text={
                  next.kind === "charge"
                    ? next.pendingMonths > 0
                      ? `הו"ק: ${next.pendingMonths} חודשים ממתינים לאישור ב"בוקר טוב" · חיוב הבא ${formatDate(next.date)}`
                      : `חיוב הו"ק הבא: ${formatDate(next.date)}`
                    : `פעיל עד: ${formatDate(next.date)}`
                }
              >
                <span className={`nivck2-date nivck2-date-${next.tone}`}>
                  {next.tone === "danger" && <WarningCircle size={12} weight="fill" />}
                  <span className="nivck2-date-main">{shortDate(next.date)}</span>
                  <span className="nivck2-date-sub">
                    {next.kind === "charge"
                      ? next.pendingMonths > 0
                        ? `${next.pendingMonths} ממתינים`
                        : 'הו"ק'
                      : next.daysLeft < 0
                        ? "פג תוקף"
                        : "סיום מסלול"}
                  </span>
                </span>
              </HoverTip>
            ) : (
              <span className="nivck2-cell-empty">—</span>
            )}
          </Cell>

          {/* Lifetime revenue */}
          <Cell col="revenue" label='סה"כ הכנסות'>
            {revenue.count > 0 ? (
              <HoverTip
                text={`${revenue.count} תשלומים · ${formatShekels(revenue.preVat)} לפני מע"מ · ${formatShekels(
                  revenue.gross
                )} כולל`}
              >
                <span className="nivck2-money">
                  <span className="nivck2-money-main">{formatShekels(revenue.preVat)}</span>
                  <span className="nivck2-money-sub">{revenue.count} תשלומים</span>
                </span>
              </HoverTip>
            ) : (
              <span className="nivck2-cell-empty">—</span>
            )}
          </Cell>

          {/* Last contact — click logs a new one */}
          <Cell col="contact" label="קשר אחרון" className="nivck2-cell-contact-anchor" onClick={stop}>
            <HoverTip
              text={
                contact.lastContactAt
                  ? `קשר אחרון: ${formatDate(contact.lastContactAt)}${
                      contact.channel ? ` · ${CONTACT_CHANNEL_LABELS[contact.channel]}` : ""
                    } · לחיצה לרישום קשר חדש`
                  : "לא נרשם קשר · לחיצה לרישום"
              }
            >
              <button
                type="button"
                className={`nivck2-when nivck2-when-${contact.tone}`}
                onClick={(e) => togglePopover(e, "contact")}
                aria-haspopup="dialog"
                aria-expanded={popover === "contact"}
              >
                {contact.lastContactAt ? getRelativeDate(contact.lastContactAt) : "ללא קשר"}
              </button>
            </HoverTip>
            {popover === "contact" && (
              <span className="nivck2-pop-anchor nivck2-pop-anchor-cell">
                <ContactQuickLogPopover
                  clinicId={row.clinicId}
                  author={author}
                  presetChannel={contact.channel || "call"}
                  onClose={() => setPopover(null)}
                />
              </span>
            )}
          </Cell>

          {/* Last login */}
          <Cell col="login" label="כניסה אחרונה">
            {usage ? (
              <HoverTip
                text={`${usage.logins?.sessions30d || 0} כניסות ב-30 יום · ${
                  usage.meetings?.past30d?.total || 0
                } פגישות ב-30 יום`}
              >
                <span className={`nivck2-when nivck2-when-login-${loginTone(loginDays)}`}>
                  {relativeDaysLabel(loginDays)}
                </span>
              </HoverTip>
            ) : (
              <span className="nivck2-cell-empty">—</span>
            )}
          </Cell>

          <CaretLeft size={14} className="nivck2-row-caret" />
        </>
      )}
    </div>
  );
}

export default ClinicRow;
